import React, { useState } from 'react';
import moment from 'moment';
import style from './schema-layout.module.scss';
import EditIcon from '../../../../../../../assets/svg/solo-chat/pencil.svg';
import { DeleteIcon } from '../../../icons';
import { Table } from 'antd';
import AddIcon from '../../../../../../../assets/svg/solo-chat/circle-plus.svg';
import { EmptyState } from './EmptyState';
import { AddIntent } from '../components';
import { useAppAndBranchId } from '../../../hooks/useAppandBranchID';
import { Modal } from '../../../components';
import { useMutate, useQuery, usePermissions } from '../../../hooks';
import { Intent } from '../types';
import { api } from '../api';
import { useToast } from '../../../../../../../helpers/commonFunction';
import { errorMessage } from '../../../utils/error';

export function Intents() {
	const { ErrorToast } = useToast();
	const [appId, branchId] = useAppAndBranchId();
	const {
		loading,
		data: intents,
		setData: setIntents,
	} = useQuery<Intent[]>(() => {
		return api.getSchema(appId, branchId, 'intent');
	}, branchId);
	const { mutate: addMutate, loading: addLoading } = useMutate(api.addSchema);
	const { mutate: editMutate, loading: editLoading } = useMutate(
		api.editSchema,
	);
	const { mutate: deleteMutate, loading: deleteLoading } = useMutate(
		api.deleteSchema,
	);

	const [canCreate, canEdit, canDelete] = usePermissions(['nlu.Create', 'nlu.Edit', 'nlu.Delete']) as boolean[]

	const dataSource = Array.isArray(intents) ? intents.map(intentMapper) : [];

	const [intentName, setIntentName] = useState('');
	const [intentId, setIntentId] = useState('');

	const [modalVisibility, setModalVisibility] = useState({
		addIntent: false,
		editIntent: false,
		deleteIntent: false,
	});
	const [error, setError] = useState('');
	const deleteIntentName = intents?.find((el) => el.id === intentId)?.name;

	function updateVisibility(key: string, visibility: boolean) {
		setModalVisibility((prev) => ({
			...prev,
			[key]: visibility,
		}));
	}

	function validateIntent(intent: string) {
		if (!intent.length) {
			setError('Enter intent name');
			return false;
		}

		const isExists = intents?.findIndex((el) => el.name === intent) !== -1;
		if (isExists) {
			setError('Intent name already exists');
			return false;
		}

		return true;
	}

	const columns = [
		{
			title: 'Intents',
			dataIndex: 'name',
			key: 'name',
		},
		{
			title: 'Labelled Utterances',
			dataIndex: 'utteranceCount',
			key: 'utteranceCount',
		},
		{
			title: 'Created At',
			dataIndex: 'createdAt',
			key: 'createdAt',
		},
		{
			title: '',
			dataIndex: 'ghost',
			key: 'ghost',
			render: (_: any, record: any) => {
				return (
					<div className="solo-chat-table-actions">
						{canEdit && (
							<button
								onClick={() => {
									setIntentId(record.id);
									updateVisibility('editIntent', true);
									setIntentName(record.name);
								}}
							>
								<img width="13" height="13" src={EditIcon} alt="edit-intent" />
							</button>
						)}
						{canDelete && (
							<button
								onClick={() => {
									setIntentId(record.id);
									updateVisibility('deleteIntent', true);
								}}
							>
								<DeleteIcon width="13" height="13" stroke="#292D32" />
							</button>
						)}
					</div>
				);
			},
		},
	];

	return (
		<div>
			<div className={style.list}>
				<Table
					dataSource={dataSource}
					columns={columns}
					pagination={false}
					rowKey="id"
					loading={loading}
				/>
				<div className={style.actions}>
					{canCreate && (
						<button
							className={style.add}
							onClick={() => updateVisibility('addIntent', true)}
						>
							<img src={AddIcon} alt="add intent" />
							<span>Add Intent</span>
						</button>
					)}
				</div>
			</div>

			<AddIntent
				error={error}
				onIntentNameChange={(intentName) => {
					setError('');
					setIntentName(intentName);
				}}
				isOpen={modalVisibility.addIntent}
				loading={addLoading}
				onOk={async () => {
					try {
						if (!validateIntent(intentName.trim())) {
							return;
						}
						const intent = await addMutate(appId, {
							name: intentName.trim(),
							branchId,
							type: 'intent',
						});
						setIntents((prev) => {
							const intents = Array.isArray(prev) ? [...prev] : [];
							intents.push(intentMapper(intent));
							return intents;
						});
						updateVisibility('addIntent', false);
					} catch (err) {
						ErrorToast(errorMessage("add", "intent", err.code));
					}
				}}
				onCancel={() => {
					setError('');
					updateVisibility('addIntent', false);
				}}
			/>

			<AddIntent
				isEdit
				error={error}
				initialIntentName={intentName}
				onIntentNameChange={(intentName) => {
					setError('');
					setIntentName(intentName);
				}}
				isOpen={modalVisibility.editIntent}
				loading={editLoading}
				onOk={async () => {
					try {
						if (!validateIntent(intentName.trim())) {
							return;
						}
						const data = await editMutate(appId, {
							name: intentName.trim(),
							id: intentId,
							branchId,
							type: 'intent',
						});
						setIntents((prev) => {
							const intents = Array.isArray(prev) ? [...prev] : [];
							return intents.map((intent) =>
								intent.id === data.id ? { ...intent, name: data.name } : intent,
							);
						});
						updateVisibility('editIntent', false);
					} catch (err) {
						ErrorToast(errorMessage("edit", "intent", err.code));
					}
				}}
				onCancel={() => {
					setError('');
					updateVisibility('editIntent', false);
				}}
			/>

			<Modal
				styles={{
					'--ok-btn-bg': '#DE0000',
					'--ok-btn-filter': 'drop-shadow(0px 8px 13px rgba(211, 0, 73, 0.18))',
				}}
				okText="Delete"
				loading={deleteLoading}
				onOk={async () => {
				  try {
						await deleteMutate(appId, branchId, 'intents', intentId)
						setIntents((prev) => {
							const intents = Array.isArray(prev) ? [...prev] : [];
							return intents.filter((intent) => intent.id !== intentId);
						});
						updateVisibility('deleteIntent', false);
					} catch(err) {
					  ErrorToast(errorMessage("delete", "intent", err.code))
					}
				}}
				onCancel={() => updateVisibility('deleteIntent', false)}
				isOpen={modalVisibility.deleteIntent}
				title="Remove Chosen Intent"
			>
				<div
					style={{
						padding: '18px 14px',
						color: '#393939',
						fontSize: 15,
						fontWeight: 500,
					}}
				>
					<p>Do you really want to remove the chosen intent?</p>
					{deleteIntentName && (
						<span className="modal-delete-entity">{deleteIntentName}</span>
					)}
				</div>
			</Modal>

			{!Boolean(dataSource.length) && !loading && <EmptyState type="intent" />}
		</div>
	)
}

function intentMapper(intent: Intent): Intent {
	return {
		...intent,
		utteranceCount: intent.utteranceCount ?? 0,
		createdAt: moment(intent.createdAt).format('DD MMM YYYY, hh:mm A'),
	};
}
